import {useLazyQuery, useMutation} from '@apollo/client';
import {CursorBasedPaginationResponse, QueryBaseTypeWithWhere} from 'api/common';
import {reminderMutations, reminderQueries} from 'api/graphql-queries';
import {useAlert} from 'hooks/useAlert';
import {ResponseViewModel} from 'models';
import {
    REMINDER_TYPE,
    ReminderCreateRequestModelInputBindingModel,
    ReminderGetAllRequest,
    ReminderModel,
    ReminderPaginationResponse,
    ReminderResponseModel,
    ReminderUpdateRequestModelInputModel,
    ReminderUserIdObjectIdPaginationResponse,
    ReminderWhereQueryModel,
} from 'models/reminder';
import {useCallback, useEffect, useState} from 'react';
import {ColorProps} from 'types/theme/extended';

export const useGetRemindersCountAPI = () => {
    const [refetch, {loading, data}] = useLazyQuery<
        {reminders: CursorBasedPaginationResponse<ReminderModel>},
        QueryBaseTypeWithWhere<ReminderWhereQueryModel>
    >(reminderQueries.REMINDERS_COUNT);

    return {
        fetchRemindersCount: refetch,
        remindersCountLoading: loading,
        remindersCountResponse: data?.reminders.totalCount,
    };
};

export const useGetOneReminderAPI = () => {
    const [state, setState] = useState<ReminderModel | undefined>();
    const [refetch, {data, loading}] = useLazyQuery<
        {oneReminder: ReminderModel},
        QueryBaseTypeWithWhere<ReminderWhereQueryModel>
    >(reminderQueries.ONE_REMINDER, {
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        if (data && data.oneReminder) {
            setState(data.oneReminder);
        }
    }, [data]);

    return {
        fetchReminder: refetch,
        reminderLoading: loading,
        reminderResponse: state,
    };
};

export const useGetRemindersAPI = () => {
    const [state, setState] = useState<ReminderUserIdObjectIdPaginationResponse | undefined>();
    const [refetch, {data, loading}] = useLazyQuery<
        {reminders: CursorBasedPaginationResponse<ReminderModel>},
        ReminderGetAllRequest
    >(reminderQueries.GET_REMINDERS, {
        fetchPolicy: 'no-cache',
    });
    useEffect(() => {
        if (!loading && data) {
            setState({
                reminders: {
                    pageInfo: data.reminders.pageInfo,
                    nodes: data.reminders.nodes,
                    edges: data.reminders.edges,
                    totalCount: data.reminders.totalCount,
                },
            });
        }
    }, [data]);

    return {
        remindersResponse: state?.reminders,
        remindersLoading: loading,
        fetchReminders: refetch,
    };
};

export const useCreateReminder = () => {
    const {invokeAlert} = useAlert();
    const [create, {data, loading}] = useMutation<
        {createReminder: ReminderResponseModel},
        {input: ReminderCreateRequestModelInputBindingModel}
    >(reminderMutations.CREATE_REMINDER);

    const createReminder = useCallback(
        (input: ReminderCreateRequestModelInputBindingModel) => {
            create({
                variables: {input},
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to create reminder');
            });
        },
        [create],
    );

    return {
        createReminder,
        createReminderLoading: loading,
        createReminderResponse: data?.createReminder,
    };
};

export const useUpdateReminder = () => {
    const {invokeAlert} = useAlert();
    const [update, {data, loading}] = useMutation<
        {updateReminder: ReminderResponseModel},
        {input: ReminderUpdateRequestModelInputModel}
    >(reminderMutations.UPDATE_REMINDER);

    const updateReminder = useCallback(
        (input: ReminderUpdateRequestModelInputModel) => {
            update({
                variables: {input},
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to update reminder');
            });
        },
        [update],
    );

    return {
        updateReminder,
        updateReminderLoading: loading,
        updateReminderResponse: data?.updateReminder,
    };
};

export const useRemoveRemindersByIds = () => {
    const {invokeAlert} = useAlert();
    const [remove, {data, loading}] = useMutation<
        {removeRemindersByIds: ResponseViewModel<string>},
        {reminderIds: string[]}
    >(reminderMutations.REMOVE_REMINDERS_BY_IDS);

    const removeReminder = useCallback(
        (reminderIds: string[]) => {
            remove({
                variables: {reminderIds},
            }).catch(error => {
                invokeAlert(error?.message ?? 'Failed to remove reminder');
            });
        },
        [remove],
    );

    return {
        removeReminder,
        removeReminderLoading: loading,
        removeReminderResponse: data,
    };
};

export const useReminderHelper = () => {
    const getReminderTypeName = useCallback((type: REMINDER_TYPE) => {
        switch (type) {
            case REMINDER_TYPE.TASK:
                return 'Ticket';
            case REMINDER_TYPE.MANUAL:
                return 'Manual';
            default:
                return 'Reminder';
        }
    }, []);
    const getChipColorByReminderStatus = useCallback((status: string): ColorProps => {
        switch (status) {
            case 'Pending':
                return 'warning';
            case 'Completed':
                return 'success';
            case 'Overdue':
                return 'error';
            default:
                return 'default';
        }
    }, []);
    return {getReminderTypeName, getChipColorByReminderStatus};
};
